import { D } from '@mobily/ts-belt'
import { useEventListener } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

import { stringify } from '@repo/utils'

import { useSortBy } from '@/hooks/useSortBy'
import { dietsService } from '@/services/dietsService'
import { useGlobalStore } from '@/store/globalStore'
import { reportError } from '@/utils/reportError'

import type {
  FetchSystemDietsResponse,
  SaveCopiedSystemDietInFileParam,
} from '@/services/dietsService'
import type { DietType, UserGroup } from '@/store/types'

import type { SystemDietListItem } from '../types'

export const useSystemDietList = () => {
  const dietType: DietType = 'system'
  const globalStore = useGlobalStore()
  const { createMessage, changeDietCounter } = globalStore
  const { user } = storeToRefs(globalStore)
  const { setSortBy, getIconBySort, sort } = useSortBy<
    keyof FetchSystemDietsResponse,
    SystemDietListItem
  >('name')
  const groups = ref<UserGroup[]>([])
  const diets = ref<SystemDietListItem[]>([])
  const hoveredDiet = ref('')
  const dragToNav = ref(false)
  const loading = ref(true)
  const copyToPatientModal = ref({ dietId: 0, visible: false })
  const copySingleDietMenu = ref({ dietId: 0, visible: false })
  const copySelectedDietMenu = ref({ visible: false })

  const sortedDiets = computed(() => [...diets.value].sort(sort))
  const allDietsSelected = computed(() => diets.value.every(diet => diet.selected))
  const someDietsSelected = computed(() => diets.value.some(diet => diet.selected))
  const selectedDietsLength = computed(() => diets.value.filter(diet => diet.selected).length)

  const loadDiets = async () => {
    loading.value = true
    const response = await dietsService.fetchSystemDiets({})
    diets.value = response.data.map(diet => D.set(diet, 'selected', false))
    loading.value = false
  }
  const loadGroups = async () => {
    groups.value = (await dietsService.fetchGroups({ dietType: 'user' })).data
  }
  const unselectAll = () => {
    diets.value.forEach(diet => {
      diet.selected = false
    })
  }
  const selectDiet = (selected: SystemDietListItem) => {
    diets.value = diets.value.map(diet =>
      diet.id === selected.id ? { ...diet, selected: !diet.selected } : diet,
    )
  }
  const handleAll = () => {
    const selected = !allDietsSelected.value
    diets.value = diets.value.map(diet => ({ ...diet, selected }))
  }
  const ghostText = () =>
    selectedDietsLength.value === 1
      ? '1 szablon do przeniesienia'
      : `${selectedDietsLength.value} szablony do przeniesienia`
  const handleDietDragStart = (diet: SystemDietListItem, event: DragEvent) => {
    if (!event.dataTransfer) return
    dragToNav.value = true
    const selectedDiets = diets.value.filter(item => item.selected).map(item => item.id)
    event.dataTransfer.dropEffect = 'move'
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('selectedDiets', stringify(selectedDiets))
    if (!diet.selected) {
      event.dataTransfer.setData('draggedDiets', stringify([diet.id]))
      unselectAll()
      selectDiet(diet)
    } else {
      event.dataTransfer.setData('draggedDiets', stringify(selectedDiets))
    }
    const ghostImage = document.getElementById('ghost-image')
    if (ghostImage) event.dataTransfer.setDragImage(ghostImage, 0, 0)
  }
  const sendSystemDietsListToFile = async (dietsIds: number[]) => {
    const email = user?.value?.email
    if (!email) return
    const payload: SaveCopiedSystemDietInFileParam[] = diets.value
      .filter(diet => dietsIds.includes(diet.id))
      .map(diet => ({ name: diet.name, type: 'system-diets', user: email }))
    try {
      await dietsService.saveCopiedSystemDietInFile(payload)
    } catch (error) {
      reportError(error, 'Error while sending system diets list to file', {
        dietsToSendList: payload,
      })
    }
  }
  const copyDiet = async (groupId: number, dietsIds: number[]) => {
    loading.value = true
    await dietsService.copyDietsToGroup({ dietsIds, dietType, groupId })
    changeDietCounter({ dietType: 'user', value: dietsIds.length })
    createMessage({ title: 'Skopiowano szablon' })
    loading.value = false
    await sendSystemDietsListToFile(dietsIds)
  }
  const handleDietDrop = async (event: DragEvent) => {
    if (!event.dataTransfer) return
    try {
      const selectedDiets = JSON.parse(event.dataTransfer.getData('selectedDiets')) as number[]
      const draggedDiets = JSON.parse(event.dataTransfer.getData('draggedDiets')) as number[]
      loading.value = true
      await dietsService.copyDietsToGroup({ dietsIds: draggedDiets, dietType, groupId: null })
      changeDietCounter({ dietType: 'user', value: draggedDiets.length })
      unselectAll()
      diets.value.forEach(diet => {
        diet.selected = selectedDiets.includes(diet.id)
      })
      loading.value = false
      await sendSystemDietsListToFile(draggedDiets)
    } catch (error) {
      reportError(error, 'Error while diet drop')
    }
  }
  const showCopyToPatientModal = (dietId: number) => {
    if (user?.value?.is_rodo_accepted) copyToPatientModal.value = { dietId, visible: true }
    else location.href = import.meta.env.VUE_APP_HOST + '/dietetyk/account/agreements'
  }
  const hideCopyToPatientModal = () => {
    copyToPatientModal.value = { dietId: 0, visible: false }
  }
  const showCopySingleDietMenu = (dietId: number) => {
    copySingleDietMenu.value = { dietId, visible: true }
  }
  const hideCopySingleDietMenu = () => {
    copySingleDietMenu.value = { dietId: 0, visible: false }
  }
  const copySingleDiet = async (groupId: number) => {
    await copyDiet(groupId, [copySingleDietMenu.value.dietId])
    hideCopySingleDietMenu()
  }
  const showCopySelectedDietMenu = () => {
    copySelectedDietMenu.value.visible = true
  }
  const hideCopySelectedDietMenu = () => {
    copySelectedDietMenu.value.visible = false
  }
  const copySelectedDiet = async (groupId: number) => {
    await copyDiet(
      groupId,
      diets.value.filter(diet => diet.selected).map(diet => diet.id),
    )
    hideCopySelectedDietMenu()
  }
  const sendSystemDiets = async (dietId: number) => sendSystemDietsListToFile([dietId])

  onMounted(async () => {
    try {
      await Promise.all([loadDiets(), loadGroups()])
    } catch (error) {
      reportError(error, 'Error while mounting diet list system')
    }
  })
  useEventListener(document, 'click', () => {
    hideCopySingleDietMenu()
    hideCopySelectedDietMenu()
  })

  return {
    allDietsSelected,
    copySelectedDiet,
    copySelectedDietMenu,
    copySingleDiet,
    copySingleDietMenu,
    copyToPatientModal,
    dietType,
    dragToNav,
    getIconBySort,
    ghostText,
    groups,
    handleAll,
    handleDietDragEnd: () => {
      dragToNav.value = false
    },
    handleDietDragStart,
    handleDietDrop,
    hideCopySelectedDietMenu,
    hideCopySingleDietMenu,
    hideCopyToPatientModal,
    isHoveredDiet: (key: string) => hoveredDiet.value === key,
    loading,
    selectDiet,
    sendSystemDiets,
    setHoverDiet: (key: string) => {
      hoveredDiet.value = key
    },
    setSortBy,
    showCopySelectedDietMenu,
    showCopySingleDietMenu,
    showCopyToPatientModal,
    someDietsSelected,
    sortedDiets,
  }
}
